import { useCallback, useEffect, useState } from "react";
import { ensureFrameDetail } from "@/lib/frame-detail-requests";

export interface FrameDetailState {
  /** The detail is on its way. */
  loading: boolean;
  /** This load failed (its API error toast has shown). */
  failed: boolean;
  /** Load it again after a failure. */
  retry: () => void;
}

/** One load: a new frame, a new need or a retry starts another. */
interface DetailRun {
  frameId: string;
  needed: boolean;
}

/**
 * Load a card-only frame's detail while `needed` (the caller passes
 * `contentState === "card"`); the wait is dropped on unmount. A failure has
 * already shown the API error toast and belongs to its own load: the next
 * one (`retry`, or the need coming back) reports loading until it settles.
 */
export function useFrameDetail(
  frameId: string,
  needed: boolean,
): FrameDetailState {
  const [run, setRun] = useState<DetailRun>({ frameId, needed });
  if (run.frameId !== frameId || run.needed !== needed) {
    setRun({ frameId, needed });
  }
  const [failedRun, setFailedRun] = useState<DetailRun | null>(null);
  useEffect(() => {
    if (!run.needed) return;
    const controller = new AbortController();
    ensureFrameDetail(run.frameId, { signal: controller.signal }).catch(() => {
      if (!controller.signal.aborted) setFailedRun(run);
    });
    return () => controller.abort();
  }, [run]);
  const retry = useCallback(() => setRun((current) => ({ ...current })), []);
  const failed = needed && failedRun === run;
  return { loading: needed && !failed, failed, retry };
}
